/**
 * Main application component.
 *
 * @stable
 */

import { fromEvent, merge } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

import { environment } from '@env/environment';

import { Store } from '@ngxs/store';

import { AuthState } from '@shared/states/auth.state';

import { ChangeDetectionStrategy, Component, ErrorHandler, inject } from '@angular/core';
import { AccountManager } from '@shared/services/account-manager.service';

import packageJson from '../../package.json';
import { ZefErrorHandler } from '@shared/error.handler';
import { EskoAgentContextService } from '@shared/modules/esko-agent/services/esko-agent-context.service';
import { SupportChatAgentContext } from '@shared/modules/esko-agent/services/support-chat-agent-context.service';
import { EskoDeepResearchService } from '@shared/modules/esko-agent/services/esko-deep-research.service';
import { GlobalSpinner } from '@shared/components/global-spinner/global-spinner.component';
import { RouterOutlet } from '@angular/router';
import { EskoAgentBubbleComponent } from '@shared/modules/esko-agent/components/chat/esko-agent-bubble.component';

@Component({
  selector: 'zef-app',
  templateUrl: './zef.component.html',
  styleUrls: ['./zef.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EskoDeepResearchService, { provide: EskoAgentContextService, useClass: SupportChatAgentContext }],
  imports: [GlobalSpinner, RouterOutlet, EskoAgentBubbleComponent],
})
export class ZefApp {
  readonly am = inject(AccountManager);
  readonly store = inject(Store);
  readonly eh = inject(ErrorHandler) as ZefErrorHandler;

  build: string = '';
  version: string = packageJson.version;

  constructor() {
    inject(EskoAgentContextService);

    if (
      window.location.href.indexOf('http://') === 0 &&
      window.location.href.indexOf('192.168.') === -1 &&
      window.location.href.indexOf('localhost') === -1
    ) {
      window.location.href = window.location.href.replace('http://', 'https://');
    } else {
      this.build = environment.debug ? 'Beta' : '';

      console.log($localize`:console log for app version@@zef-i18n-00000:App version: ${this.version}:INTERPOLATION:`);
    }

    merge(
      fromEvent(document, 'wheel', { capture: true, passive: true }),
      fromEvent(document, 'scroll', { capture: true, passive: true }),
      fromEvent(document, 'keydown', { capture: true, passive: true }),
      fromEvent(document, 'mousedown', { capture: true, passive: true }),
      fromEvent(document, 'mousemove', { capture: true, passive: true }),
      fromEvent(document, 'touchstart', { capture: true, passive: true }),
    )
      .pipe(throttleTime(5 * 60 * 1000))
      .subscribe(() => {
        const userInfo = this.store.selectSnapshot(AuthState.info);

        if (userInfo) {
          this.am.updateLastActivityTime(userInfo.uid);
        }
      });
  }
}
